import { useTranslations } from '@vocab/react';
import {
  Badge,
  Button,
  Card,
  Heading,
  IconSend,
  Inline,
  Stack,
  Text,
  TooltipRenderer,
} from 'braid-design-system';

import appTranslations from 'src/App/App.vocab';
import { type UserDetails, UserStatus } from 'src/graphql/generated';
import { useUserActions } from 'src/hooks/use-user-actions';

import translations from './.vocab';

export interface UserDetailsCardProps {
  user: Omit<UserDetails, 'permissions'>;
}

export const UserDetailsCard = ({ user }: UserDetailsCardProps) => {
  const { handleResendActivationEmail } = useUserActions();
  const { t } = useTranslations(translations);
  const { t: appTrans } = useTranslations(appTranslations);

  return (
    <Stack space="large">
      <Heading level="3">{appTrans('Individual details')}</Heading>
      <Card>
        <Stack space="large">
          <Stack space="small">
            <Inline space="small" alignY="center">
              <Text weight="strong">{appTrans('Email')}</Text>
              {user.status === UserStatus.PENDING && (
                <TooltipRenderer
                  id="pending-tooltip"
                  tooltip={<Text>{appTrans('Pending user description')}</Text>}
                >
                  {({ triggerProps }) => (
                    <Badge tone="caution" {...triggerProps}>
                      {appTrans('Pending activation badge')}
                    </Badge>
                  )}
                </TooltipRenderer>
              )}
            </Inline>
            <Text data={{ 'dd-privacy': 'mask', 'hj-masked': true }}>
              {user.email}
            </Text>
          </Stack>
          <Stack space="small">
            <Text weight="strong">{t('Name')}</Text>
            <Text data={{ 'dd-privacy': 'mask', 'hj-masked': true }}>
              {user.firstName} {user.lastName}
            </Text>
          </Stack>
          {user.status === UserStatus.PENDING && (
            <Inline space="small">
              <Button
                variant="soft"
                size="small"
                onClick={() => handleResendActivationEmail(user)}
                icon={<IconSend />}
              >
                {t('Resend activation email')}
              </Button>
            </Inline>
          )}
        </Stack>
      </Card>
    </Stack>
  );
};
